import _ from 'lodash'

import stopBundles from '../../../utils/stopBundles.js'

// Sortable items standing in for the draggable lines that mark the fixed zone at the start and the end of the route
export const ANCHOR_START_ID = '__anchorStart__'
export const ANCHOR_END_ID = '__anchorEnd__'

export function isAnchorLine(item) {
    return item?.id === ANCHOR_START_ID || item?.id === ANCHOR_END_ID
}

// Consecutive stops at the same address are shown as one bundle, like the messenger sees them in the app. An opened
// bundle is followed by its stops, so the planner can drag a single stop out of the bundle.
function bundleStopItems(addresses, expandedBundles) {
    const items = []

    // getStopBundles marks the stops with their bundle, so it gets copies to keep the route in the store untouched
    stopBundles.getStopBundles(addresses.map((address) => ({...address}))).map((item) => {
        if (!item.isBundle) {
            items.push(item)
            return
        }

        items.push({...item, hasArrived: item.addresses.every((address) => address.hasArrived)})

        if (expandedBundles[item.id]) {
            items.push(...item.addresses)
        }
    })

    return items
}

// The items of the sortable list in the route panel: the stops and bundles, with the lines of the fixed zone
export function getStopItems(route, expandedBundles) {
    // Completed stops are bundled separately, they can not be moved anymore
    const stopItems = bundleStopItems(route?.completedAddresses || [], expandedBundles)
    const routeAddresses = route?.addresses || []

    if (route?.anchorsEnabled) {
        const firstFlexibleIndex = routeAddresses.findIndex((address) => address.anchor !== 'start')
        const firstAnchoredEndIndex = routeAddresses.findIndex((address) => address.anchor === 'end')

        const startLineIndex = firstFlexibleIndex === -1 ? routeAddresses.length : firstFlexibleIndex
        const endLineIndex = firstAnchoredEndIndex === -1 ? routeAddresses.length : firstAnchoredEndIndex

        // A bundle never crosses one of the lines of the fixed zone
        stopItems.push(
            ...bundleStopItems(routeAddresses.slice(0, startLineIndex), expandedBundles),
            {id: ANCHOR_START_ID},
            ...bundleStopItems(routeAddresses.slice(startLineIndex, endLineIndex), expandedBundles),
            {id: ANCHOR_END_ID},
            ...bundleStopItems(routeAddresses.slice(endLineIndex), expandedBundles)
        )
    } else {
        stopItems.push(...bundleStopItems(routeAddresses, expandedBundles))
    }

    return stopItems
}

// Turns the sortable list after dragging the item at newIndex back into the stops of the route, in their new order
export function getDraggedAddresses(items, newIndex, expandedBundles, anchorsEnabled) {
    const draggedItem = items[newIndex]

    const startLineIndex = items.findIndex((item) => item.id === ANCHOR_START_ID)
    const endLineIndex = items.findIndex((item) => item.id === ANCHOR_END_ID)

    // Dragging one line past the other leaves an empty flexible part instead of an inverted zone
    if (startLineIndex !== -1 && endLineIndex !== -1 && startLineIndex > endLineIndex) {
        items = [...items]

        const draggedLineIndex = draggedItem.id === ANCHOR_START_ID ? startLineIndex : endLineIndex
        const otherLineIndex = draggedItem.id === ANCHOR_START_ID ? endLineIndex : startLineIndex

        items.splice(otherLineIndex, 0, ...items.splice(draggedLineIndex, 1))
    }

    // Everything above the start line and below the end line ends up in the fixed zone
    let anchor = anchorsEnabled ? 'start' : undefined
    const addresses = []

    items.map((item) => {
        if (item.id === ANCHOR_START_ID) {
            anchor = undefined
        } else if (item.id === ANCHOR_END_ID) {
            anchor = 'end'
        } else {
            let itemAddresses = [item]

            if (item.isBundle) {
                // The stops of an opened bundle are separate items, unless the bundle itself is dragged: then they move along with it
                itemAddresses = !expandedBundles[item.id] || item === draggedItem ? item.addresses : []
            } else if (draggedItem.isBundle && item.bundleId === draggedItem.id) {
                itemAddresses = []
            }

            itemAddresses.map((itemAddress) => {
                const address = _.omit(itemAddress, 'bundleId')

                addresses.push(address.hasArrived ? address : {...address, anchor})
            })
        }
    })

    return addresses
}
